import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import '../assets/ClaimDetailModal.css';

const ClaimDetailModal = ({ claim, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [claimTypes, setClaimTypes] = useState([]);

  // --- Parent cost-recovery state (admin only) ---
  const [recoverFromParent, setRecoverFromParent] = useState(false);
  const [recoveryTutees, setRecoveryTutees] = useState([]);      // [{ tuteeId, tuteeName, tuteeCode, drafts: [...] }]
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [selectedTuteeId, setSelectedTuteeId] = useState('');
  const [selectedDraftKey, setSelectedDraftKey] = useState('');  // `${month}-${year}`
  const [recoveryLabel, setRecoveryLabel] = useState('');        // admin-defined line item shown on the parent invoice

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    setUserRole(user.role || '');
    fetchClaimTypes();
    
    // Prevent background scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Lazily load selectable draft invoices the first time an admin opts to recover.
  const loadRecoveryOptions = async () => {
    if (recoveryTutees.length > 0 || recoveryLoading) return;
    setRecoveryLoading(true);
    try {
      const response = await adminApi.getClaimRecoveryOptions();
      setRecoveryTutees(response.data?.data?.tutees || []);
    } catch (err) {
      console.error('Error loading recovery options:', err);
      setError(err.response?.data?.message || 'Failed to load draft invoices for recovery');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleToggleRecovery = async (checked) => {
    setRecoverFromParent(checked);
    setError('');
    if (checked) {
      await loadRecoveryOptions();
    } else {
      setSelectedTuteeId('');
      setSelectedDraftKey('');
      setRecoveryLabel('');
    }
  };

  const selectedTutee = recoveryTutees.find(t => t.tuteeId === selectedTuteeId);

  const fetchClaimTypes = async () => {
    try {
      const response = await adminApi.getClaimTypes();
      setClaimTypes(response.data.data);
    } catch (error) {
      console.error('Error fetching claim types:', error);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleApproveClaim = async () => {
    // Build the recovery selection if the admin opted in.
    let recovery = null;
    if (recoverFromParent) {
      if (!selectedTuteeId || !selectedDraftKey) {
        setError('Select a tutee and a draft invoice month to recover the cost from.');
        return;
      }
      const label = recoveryLabel.trim();
      if (label && /<[^>]*>|javascript\s*:|on\w+\s*=|data\s*:/i.test(label)) {
        setError('The invoice line description contains invalid characters.');
        return;
      }
      if (label.length > 200) {
        setError('The invoice line description must be 200 characters or fewer.');
        return;
      }
      const [month, year] = selectedDraftKey.split('-').map(Number);
      recovery = {
        recoverFromParent: true,
        recoveryTutee: selectedTuteeId,
        recoveryMonth: month,
        recoveryYear: year,
        recoveryLabel: label
      };
    }

    const notes = prompt('Approval notes (optional):');
    if (notes === null) return; // User cancelled

    setLoading(true);
    setError('');

    try {
      await adminApi.approveClaim(claim._id, notes, recovery);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error approving claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to approve claim');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClaim = async () => {
    const reason = prompt('Rejection reason (optional):');
    if (reason === null) return;

    setLoading(true);
    setError('');

    try {
      await adminApi.rejectClaim(claim._id, reason);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error rejecting claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to reject claim');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClaim = async () => {
    const reason = prompt('Cancellation reason (required):');
    if (!reason || reason.trim().length === 0) {
      setError('Cancellation reason is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await adminApi.cancelClaim(claim._id, reason);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error cancelling claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to cancel claim');
    } finally {
      setLoading(false);
    }
  };

  const canApproveClaim = () => {
    // Only admins may approve/reject claims. Tutors (including profit sharers)
    // can view claims but the approval decision is admin-only.
    return userRole === 'admin';
  };

  const canCancelClaim = () => {
    return claim.status === 'pending' && currentUser && claim.claimant._id === currentUser.id;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type) => {
    const claimType = claimTypes.find(ct => ct.value === type);
    return claimType ? claimType.label : type;
  };

  const getMonthName = (month) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1] || '';
  };

  // Reference-only breakdown of where an approved claim's cost was taken from.
  // Driven by the costDistribution snapshot persisted at approval time.
  const renderCostDistribution = () => {
    const cd = claim.costDistribution;
    if (!cd || !cd.method) return null;

    const appliedOn = cd.appliedAt ? formatDate(cd.appliedAt) : null;
    const addedTo = (cd.addedToMonth && cd.addedToYear)
      ? `${getMonthName(cd.addedToMonth)} ${cd.addedToYear}`
      : null;

    let heading = 'Cost Distribution';
    let body = null;

    if (cd.method === 'profit_share_split') {
      heading = 'Cost Distribution — shared across profit sharers';
      body = (
        <table className="cost-distribution-table">
          <thead>
            <tr>
              <th>Profit Sharer</th>
              <th>Share</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(cd.shares || []).map((s, i) => (
              <tr key={i}>
                <td>
                  {s.name}
                  {s.isClaimant && <span className="cd-claimant-tag"> (claimant)</span>}
                </td>
                <td>{s.percentage}%</td>
                <td>{formatAmount(s.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (cd.method === 'personal') {
      heading = 'Cost Distribution — personal (not shared)';
      body = (
        <p className="cost-distribution-note">
          Full amount of {formatAmount(cd.totalAmount)} added to the claimant's invoice only.
          Other profit sharers were not charged.
        </p>
      );
    } else if (cd.method === 'parent_recovery') {
      heading = 'Cost Distribution — recovered from parent';
      const pr = cd.parentRecovery || {};
      const who = pr.tuteeName
        ? `${pr.tuteeName}${pr.tuteeCode ? ` (${pr.tuteeCode})` : ''}`
        : 'parent invoice';
      const period = (pr.month && pr.year) ? ` ${getMonthName(pr.month)} ${pr.year}` : '';
      body = (
        <p className="cost-distribution-note">
          {formatAmount(pr.amount != null ? pr.amount : cd.totalAmount)} charged to{' '}
          <strong>{who}</strong>'s invoice{period}
          {pr.invoiceNumber ? ` (${pr.invoiceNumber})` : ''}.
          {' '}The tutor was reimbursed the full amount; profit sharers were not charged.
        </p>
      );
    }

    return (
      <div className="cost-distribution-section">
        <h4 className="cost-distribution-heading">{heading}</h4>
        {body}
        <div className="cost-distribution-meta">
          {addedTo && <span>Added to: {addedTo}</span>}
          {appliedOn && <span>Applied on: {appliedOn}</span>}
        </div>
      </div>
    );
  };

  const renderOutcome = () => {
    if (claim.status === 'pending') {
      return (
        <div className="outcome-section outcome-pending">
          <h3>Outcome</h3>
          <p className="outcome-status">Pending approval ({claim.approvedCount || 0}/{claim.requiredApprovals || 1})</p>
        </div>
      );
    }

    if (claim.status === 'approved') {
      const processedMonth = claim.processedToMonth || claim.month;
      const processedYear = claim.processedToYear || claim.year;
      return (
        <div className="outcome-section outcome-approved">
          <h3>Outcome</h3>
          <table className="claim-info-table">
            <tbody>
              <tr>
                <td className="info-label">Status</td>
                <td className="info-value">Approved</td>
              </tr>
              <tr>
                <td className="info-label">Approved On</td>
                <td className="info-value">{claim.finalApprovedAt ? formatDate(claim.finalApprovedAt) : formatDate(claim.updatedAt)}</td>
              </tr>
              {claim.processedToPaymentAdjustment && (
                <tr>
                  <td className="info-label">Added to Invoice</td>
                  <td className="info-value">{getMonthName(processedMonth)} {processedYear}</td>
                </tr>
              )}
              {!claim.processedToPaymentAdjustment && (
                <tr>
                  <td className="info-label">Payment</td>
                  <td className="info-value" style={{ color: '#d97706' }}>Awaiting processing</td>
                </tr>
              )}
            </tbody>
          </table>
          {renderCostDistribution()}
        </div>
      );
    }

    if (claim.status === 'rejected') {
      return (
        <div className="outcome-section outcome-rejected">
          <h3>Outcome</h3>
          <table className="claim-info-table">
            <tbody>
              <tr>
                <td className="info-label">Status</td>
                <td className="info-value" style={{ color: '#dc2626' }}>Rejected</td>
              </tr>
              <tr>
                <td className="info-label">Rejected By</td>
                <td className="info-value">{claim.rejectedBy?.name || 'Unknown'}</td>
              </tr>
              <tr>
                <td className="info-label">Rejected On</td>
                <td className="info-value">{claim.rejectedAt ? formatDate(claim.rejectedAt) : formatDate(claim.updatedAt)}</td>
              </tr>
              {claim.rejectionReason && (
                <tr>
                  <td className="info-label">Reason</td>
                  <td className="info-value reason-text">{claim.rejectionReason}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (claim.status === 'cancelled') {
      return (
        <div className="outcome-section outcome-cancelled">
          <h3>Outcome</h3>
          <table className="claim-info-table">
            <tbody>
              <tr>
                <td className="info-label">Status</td>
                <td className="info-value">Cancelled</td>
              </tr>
              <tr>
                <td className="info-label">Cancelled By</td>
                <td className="info-value">{claim.cancelledBy?.name || 'Unknown'}</td>
              </tr>
              <tr>
                <td className="info-label">Cancelled On</td>
                <td className="info-value">{claim.cancelledAt ? formatDate(claim.cancelledAt) : formatDate(claim.updatedAt)}</td>
              </tr>
              {claim.cancellationReason && (
                <tr>
                  <td className="info-label">Reason</td>
                  <td className="info-value reason-text">{claim.cancellationReason}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  const renderApprovalProgress = () => {
    if (claim.status !== 'pending') return null;

    const percentage = claim.approvalProgress?.percentage || 0;
    
    return (
      <div className="approval-section">
        <h3>Approval Progress</h3>
        <div className="approval-progress-large">
          <div className="progress-bar-large">
            <div 
              className="progress-fill-large" 
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
          <span className="progress-text-large">
            {claim.approvedCount}/{claim.requiredApprovals} Approvals
          </span>
        </div>
        
        {claim.approvals && claim.approvals.length > 0 && (
          <div className="approvals-list">
            <h4 style={{ margin: '0 0 15px 0', color: '#666', fontSize: '14px' }}>
              Approvals Received:
            </h4>
            {claim.approvals.map((approval, index) => (
              <div key={index} className="approval-item">
                <div className="approval-info">
                  <div className="approval-name">{approval.approver.name}</div>
                  <div className="approval-date">{formatDate(approval.approvedAt)}</div>
                  {approval.notes && (
                    <div className="approval-notes">"{approval.notes}"</div>
                  )}
                </div>
                <span className={`approval-status ${approval.status}`}>
                  {approval.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderRejectionInfo = () => {
    if (claim.status !== 'rejected') return null;

    return (
      <div className="rejection-section">
        <h3>Rejection Details</h3>
        <div className="rejection-info">
          <span className="rejection-by">
            Rejected by: {claim.rejectedBy?.name || 'Unknown'}
          </span>
          <span className="rejection-date">
            {formatDate(claim.rejectedAt)}
          </span>
        </div>
        <p className="rejection-reason">{claim.rejectionReason}</p>
      </div>
    );
  };

  const renderCancellationInfo = () => {
    if (claim.status !== 'cancelled') return null;

    return (
      <div className="cancellation-section">
        <h3>Cancellation Details</h3>
        <div className="cancellation-info">
          <span className="cancellation-by">
            Cancelled by: {claim.cancelledBy?.name || 'Unknown'}
          </span>
          <span className="cancellation-date">
            {formatDate(claim.cancelledAt)}
          </span>
        </div>
        <p className="cancellation-reason">{claim.cancellationReason}</p>
      </div>
    );
  };

  const renderPaymentAdjustmentInfo = () => {
    if (!claim.processedToPaymentAdjustment) return null;

    return (
      <div className="payment-adjustment-info">
        <h3>✅ Processed to Payment Adjustment</h3>
        <p>
          This claim has been approved and processed into a payment adjustment. 
          The amount will be included in the claimant's next invoice
          {claim.affectsOtherProfitSharers === false
            ? ' as a personal adjustment (other profit sharers are not affected).'
            : ' and deducted from profit sharers\' allocations proportionally.'}
        </p>
        {claim.processedAt && (
          <p style={{ marginTop: '10px', fontSize: '14px' }}>
            Processed on: {formatDate(claim.processedAt)}
          </p>
        )}
      </div>
    );
  };

  const renderActionButtons = () => {
    if (claim.status !== 'pending') return null;

    const buttons = [];

    if (canApproveClaim()) {
      buttons.push(
        <button
          key="approve"
          className="btn btn-approve"
          onClick={handleApproveClaim}
          disabled={loading}
        >
          {loading ? 'Approving...' : 'Approve'}
        </button>
      );

      buttons.push(
        <button
          key="reject"
          className="btn btn-reject"
          onClick={handleRejectClaim}
          disabled={loading}
        >
          {loading ? 'Rejecting...' : 'Reject'}
        </button>
      );
    }

    if (canCancelClaim()) {
      buttons.push(
        <button
          key="cancel"
          className="btn btn-cancel-claim"
          onClick={handleCancelClaim}
          disabled={loading}
        >
          {loading ? 'Cancelling...' : 'Cancel Claim'}
        </button>
      );
    }

    if (buttons.length === 0) return null;

    // Only admins may recover the cost from a parent's draft invoice.
    const showRecovery = userRole === 'admin';

    return (
      <div className="claim-actions">
        {showRecovery && (
          <div className="claim-recovery-panel">
            <label className="recovery-toggle">
              <input
                type="checkbox"
                checked={recoverFromParent}
                onChange={(e) => handleToggleRecovery(e.target.checked)}
                disabled={loading}
              />
              <span>Recover this cost from a parent's upcoming (draft) invoice</span>
            </label>

            {recoverFromParent && (
              <div className="recovery-fields">
                <p className="recovery-hint">
                  The full claim amount ({formatAmount(claim.amount)}) will be added as a charge on
                  the selected draft invoice. The cost will not be deducted from the profit sharers.
                </p>

                {recoveryLoading && <p className="recovery-loading">Loading draft invoices…</p>}

                {!recoveryLoading && recoveryTutees.length === 0 && (
                  <p className="recovery-empty">No draft invoices are available to recover from.</p>
                )}

                {!recoveryLoading && recoveryTutees.length > 0 && (
                  <>
                    <div className="recovery-field">
                      <label>Tutee</label>
                      <select
                        value={selectedTuteeId}
                        onChange={(e) => {
                          setSelectedTuteeId(e.target.value);
                          setSelectedDraftKey('');
                        }}
                        disabled={loading}
                      >
                        <option value="">Select a tutee…</option>
                        {recoveryTutees.map(t => (
                          <option key={t.tuteeId} value={t.tuteeId}>
                            {t.tuteeName}{t.tuteeCode ? ` (${t.tuteeCode})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedTutee && (
                      <div className="recovery-field">
                        <label>Draft invoice month</label>
                        <select
                          value={selectedDraftKey}
                          onChange={(e) => setSelectedDraftKey(e.target.value)}
                          disabled={loading}
                        >
                          <option value="">Select a draft month…</option>
                          {selectedTutee.drafts.map(d => (
                            <option key={`${d.month}-${d.year}`} value={`${d.month}-${d.year}`}>
                              {getMonthName(d.month)} {d.year} — {d.invoiceNumber} (currently {formatAmount(d.currentAmount)})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedTutee && (
                      <div className="recovery-field">
                        <label>Invoice line description (shown to the parent)</label>
                        <input
                          type="text"
                          value={recoveryLabel}
                          onChange={(e) => setRecoveryLabel(e.target.value)}
                          placeholder="e.g. Assessment books"
                          maxLength={200}
                          disabled={loading}
                        />
                        <small className="recovery-field-hint">
                          Optional. This is the text the parent sees on their invoice. Leave blank to use a default description.
                        </small>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="claim-action-buttons">
          {buttons}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content claim-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Claim Details</h2>
          <button className="close-btn" onClick={onClose} disabled={loading}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error">{error}</div>
          )}

          <div className="claim-detail-status-bar">
            <span className={`claim-status-badge ${claim.status}`}>
              {claim.displayStatus || claim.status}
            </span>
            <span className="claim-submitted-info">
              Submitted by {claim.claimant.name} · {new Date(claim.createdAt).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>

          <table className="claim-info-table">
            <tbody>
              <tr>
                <td className="info-label">Claimant</td>
                <td className="info-value">{claim.claimant.name} ({claim.claimant.email})</td>
              </tr>
              <tr>
                <td className="info-label">Amount</td>
                <td className="info-value amount">{formatAmount(claim.amount)}</td>
              </tr>
              <tr>
                <td className="info-label">Type</td>
                <td className="info-value type">{getTypeLabel(claim.type)}</td>
              </tr>
              <tr>
                <td className="info-label">Item Name</td>
                <td className="info-value">{claim.itemName}</td>
              </tr>
              <tr>
                <td className="info-label">Purchase Date</td>
                <td className="info-value">{formatDate(claim.purchaseDate)}</td>
              </tr>
              <tr>
                <td className="info-label">Claim Period</td>
                <td className="info-value">{getMonthName(claim.month)} {claim.year}</td>
              </tr>
              <tr>
                <td className="info-label">Shared Cost</td>
                <td className="info-value">{claim.affectsOtherProfitSharers === false ? 'No' : 'Yes'}</td>
              </tr>
              {claim.reason && (
                <tr>
                  <td className="info-label">Reason</td>
                  <td className="info-value reason-text">{claim.reason}</td>
                </tr>
              )}
            </tbody>
          </table>

          {renderOutcome()}
          {renderApprovalProgress()}
          {renderActionButtons()}
        </div>
      </div>
    </div>
  );
};

export default ClaimDetailModal;